import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ApartmentOutlined as FacilityIcon,
  SaveOutlined as SaveIcon,
} from '@mui/icons-material';
import { APP_SHAPE } from '../../constants/shape';
import { SAUDI_CITIES } from '../../constants/saudiCities';
import { useAuthStore } from '../../stores/auth.store';
import type { UserSchema } from '../../types/auth.type';
import type { Facility, FacilityInput, FacilityStatus, FacilityType } from '../../types/facility.type';
import { parseGoogleMapsLocation } from '../../utils/googleMapsLink';
import { userHasAnyLegacyRole } from '../../utils/permissions.helpers';
import { LocationMapPicker } from '../containers/LocationMapPicker';

interface FacilityFormDialogProps {
  open: boolean;
  orgId: string;
  facility?: Facility | null;
  saving: boolean;
  submitError?: string | null;
  onClose: () => void;
  onSubmit: (input: FacilityInput) => Promise<boolean>;
}

interface ManagerOption {
  id: string;
  label: string;
  secondary: string | null;
}

const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: `${APP_SHAPE.radiusSm}px` } } as const;

function getUserId(user: UserSchema): string {
  return user.sub || user.id || '';
}

function getUserDisplayName(user: UserSchema): string {
  const first = (user.data as { first_name?: string } | undefined)?.first_name?.trim();
  const last = (user.data as { last_name?: string } | undefined)?.last_name?.trim();
  const fullName = user.full_name?.trim() || [first, last].filter(Boolean).join(' ');
  return fullName || user.username || getUserId(user);
}

// The two ops-manager roles eligible to be a facility manager.
function isEligibleManager(user: UserSchema): boolean {
  return userHasAnyLegacyRole(user, ['recycler_ops_manager', 'truck_co_ops_manager']);
}

function toManagerOption(user: UserSchema): ManagerOption {
  return { id: getUserId(user), label: getUserDisplayName(user), secondary: user.username ?? null };
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
      {children}
    </Typography>
  );
}

export default function FacilityFormDialog({
  open,
  orgId,
  facility,
  saving,
  submitError,
  onClose,
  onSubmit,
}: FacilityFormDialogProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { getOrganizationUsers } = useAuthStore();

  const [name, setName] = useState('');
  const [type, setType] = useState<FacilityType>('recycler_facility');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [latitude, setLatitude] = useState<number | undefined>(undefined);
  const [longitude, setLongitude] = useState<number | undefined>(undefined);
  const [googleMapsUrl, setGoogleMapsUrl] = useState('');
  const [linkImportError, setLinkImportError] = useState<string | null>(null);
  const [managerUserId, setManagerUserId] = useState('');
  const [status, setStatus] = useState<FacilityStatus>('active');
  const [submitted, setSubmitted] = useState(false);

  const [managerOptions, setManagerOptions] = useState<UserSchema[]>([]);
  const [managersLoading, setManagersLoading] = useState(false);
  const [managersError, setManagersError] = useState(false);

  const loadManagers = useCallback(async () => {
    if (!orgId) return;
    setManagersLoading(true);
    setManagersError(false);
    try {
      const users = await getOrganizationUsers(orgId);
      setManagerOptions(users.filter(isEligibleManager));
    } catch {
      setManagersError(true);
    } finally {
      setManagersLoading(false);
    }
  }, [orgId, getOrganizationUsers]);

  useEffect(() => {
    if (!open) return;
    setName(facility?.name ?? '');
    setType(facility?.type ?? 'recycler_facility');
    setAddress(facility?.address ?? '');
    setCity(facility?.city ?? '');
    setLatitude(facility?.latitude ?? undefined);
    setLongitude(facility?.longitude ?? undefined);
    setGoogleMapsUrl(facility?.google_maps_url ?? '');
    setLinkImportError(null);
    setManagerUserId(facility?.manager_user_id ?? '');
    setStatus(facility?.status === 'archived' ? 'archived' : 'active');
    setSubmitted(false);
    setManagerOptions([]);
    setManagersError(false);
    void loadManagers();
  }, [open, facility, loadManagers]);

  const managerSelectOptions = useMemo<ManagerOption[]>(() => {
    const options = managerOptions.map(toManagerOption);
    // Keep the currently assigned manager selectable even if a role change
    // dropped them out of the eligible-managers fetch above.
    if (facility?.manager && !options.some((option) => option.id === facility.manager!.id)) {
      options.unshift({
        id: facility.manager.id,
        label: facility.manager.full_name || facility.manager.username || facility.manager.id,
        secondary: facility.manager.username,
      });
    }
    return options;
  }, [managerOptions, facility]);

  const selectedManager = managerSelectOptions.find((option) => option.id === managerUserId) ?? null;
  const isValid = name.trim().length > 0;

  const handleImportLink = () => {
    const result = parseGoogleMapsLocation(googleMapsUrl);
    if (result.ok) {
      setLatitude(result.latitude);
      setLongitude(result.longitude);
      setLinkImportError(null);
    } else if (result.reason === 'short-link') {
      setLinkImportError(t('facilities.form.linkErrorShort'));
    } else if (result.reason === 'not-found') {
      setLinkImportError(t('facilities.form.linkErrorNotFound'));
    }
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!isValid) return;
    const success = await onSubmit({
      name: name.trim(),
      type,
      address: address.trim() || null,
      city: city || null,
      latitude: latitude ?? null,
      longitude: longitude ?? null,
      google_maps_url: googleMapsUrl.trim() || null,
      manager_user_id: managerUserId || null,
      status,
    });
    if (success) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="md"
      scroll="paper"
      PaperProps={{
        elevation: 0,
        sx: {
          border: 1,
          borderColor: 'divider',
          maxHeight: { sm: '92vh' },
          borderRadius: { sm: `${APP_SHAPE.radiusXl}px` },
        },
      }}
    >
      <DialogTitle sx={{ px: 2.5, py: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: `${APP_SHAPE.radiusXl}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: 'primary.main',
            }}
          >
            <FacilityIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
              {facility ? t('facilities.form.editTitle') : t('facilities.form.addTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {facility ? t('facilities.form.editSubtitle') : t('facilities.form.addSubtitle')}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack spacing={3}>
          {submitError && <Alert severity="error">{submitError}</Alert>}

          <TextField
            label={t('facilities.form.name')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
            required
            fullWidth
            error={submitted && !name.trim()}
            helperText={submitted && !name.trim() ? t('facilities.form.nameError') : ' '}
            sx={fieldSx}
          />

          <Box>
            <FieldLabel>{t('facilities.form.type')}</FieldLabel>
            <ToggleButtonGroup
              value={type}
              exclusive
              fullWidth
              onChange={(_, value: FacilityType | null) => value && setType(value)}
            >
              <ToggleButton value="recycler_facility" sx={{ textTransform: 'none', fontWeight: 600 }}>
                {t('facilities.type.recycler_facility')}
              </ToggleButton>
              <ToggleButton value="warehouse" sx={{ textTransform: 'none', fontWeight: 600 }}>
                {t('facilities.type.warehouse')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 2 }}>
            <TextField
              label={t('facilities.form.address')}
              value={address}
              onChange={(event) => setAddress(event.target.value)}
              helperText={t('suppliers.form.optional')}
              sx={fieldSx}
            />
            <FormControl sx={fieldSx}>
              <InputLabel id="facility-city-label">{t('facilities.form.city')}</InputLabel>
              <Select
                labelId="facility-city-label"
                label={t('facilities.form.city')}
                value={city}
                onChange={(event) => setCity(event.target.value)}
                displayEmpty
              >
                <MenuItem value="">{t('suppliers.form.optional')}</MenuItem>
                {SAUDI_CITIES.map((cityName) => (
                  <MenuItem key={cityName} value={cityName}>{cityName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <FieldLabel>{t('facilities.form.location')}</FieldLabel>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              {t('facilities.form.locationHint')}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                label={t('facilities.form.googleMapsLink')}
                placeholder="https://maps.google.com/..."
                value={googleMapsUrl}
                onChange={(event) => {
                  setGoogleMapsUrl(event.target.value);
                  setLinkImportError(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleImportLink();
                  }
                }}
                error={Boolean(linkImportError)}
                sx={fieldSx}
              />
              <Button
                variant="outlined"
                onClick={handleImportLink}
                disabled={!googleMapsUrl.trim()}
                sx={{ flexShrink: 0 }}
              >
                {t('facilities.form.importLocation')}
              </Button>
            </Stack>
            {linkImportError && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1.5 }}>
                {linkImportError}
              </Typography>
            )}
            <LocationMapPicker
              latitude={latitude}
              longitude={longitude}
              onLocationChange={(lat, lng) => {
                setLatitude(Number(lat.toFixed(6)));
                setLongitude(Number(lng.toFixed(6)));
              }}
              height={320}
            />
            {latitude != null && longitude != null && (
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {latitude.toFixed(6)}, {longitude.toFixed(6)}
                </Typography>
                <Button size="small" onClick={() => { setLatitude(undefined); setLongitude(undefined); }}>
                  {t('facilities.form.clearLocation')}
                </Button>
              </Stack>
            )}
          </Box>

          <Divider />

          <Box>
            {managersError && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                action={<Button color="inherit" size="small" onClick={() => void loadManagers()}>{t('suppliers.details.retry')}</Button>}
              >
                {t('facilities.form.managerLoadError')}
              </Alert>
            )}
            <Autocomplete
              options={managerSelectOptions}
              value={selectedManager}
              loading={managersLoading}
              disabled={managersLoading || (managersError && managerSelectOptions.length === 0)}
              onChange={(_, value) => setManagerUserId(value?.id ?? '')}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>{option.label}</Typography>
                    {option.secondary ? (
                      <Typography variant="caption" color="text.secondary" noWrap>{option.secondary}</Typography>
                    ) : null}
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('facilities.form.manager')}
                  placeholder={t('facilities.form.managerPlaceholder')}
                  helperText={t('facilities.form.managerHelper')}
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {managersLoading ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          {facility && (
            <>
              <Divider />
              <Box>
                <FieldLabel>{t('facilities.form.status')}</FieldLabel>
                <ToggleButtonGroup
                  value={status}
                  exclusive
                  fullWidth
                  onChange={(_, value: FacilityStatus | null) => value && setStatus(value)}
                >
                  <ToggleButton value="active" sx={{ textTransform: 'none', fontWeight: 600 }}>
                    {t('facilities.status.active')}
                  </ToggleButton>
                  <ToggleButton value="archived" sx={{ textTransform: 'none', fontWeight: 600 }}>
                    {t('facilities.status.archived')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} disabled={saving}>{t('facilities.form.cancel')}</Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress color="inherit" size={16} /> : <SaveIcon />}
          onClick={() => void handleSubmit()}
          disabled={saving}
        >
          {saving ? t('facilities.form.saving') : facility ? t('facilities.form.save') : t('facilities.form.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
